import React, { useCallback, useMemo } from 'react';
import cuid from 'cuid';
import { WildcardValues } from 'shared/enums/WildcardOptions';
import WildcardTag from './WildcardTag';
import { Box, VStack } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import { Textarea } from '@televet/kibble-ui/build/components/Textarea';
import Linkify from 'linkifyjs/react';
import * as linkify from 'linkifyjs';

export enum WildcardInputQuestionTypes {
  'Intro',
  'Question',
  'Message',
  'Option',
  'Preview',
}

export enum WildcardInputPreviewModes {
  Form = 'Form',
  Survey = 'Survey',
}

interface IWildcardTextInputProps {
  text: string;
  type: WildcardInputQuestionTypes;
  showPreview?: boolean | undefined;
  textAreaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
  allowFillInTheBlanks?: boolean;
  previewMode?: WildcardInputPreviewModes;
  placeholder?: string;
  errorMessage?: string;
  handleChange?: (newValue: string) => void;
  setShowPreview?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  validate?: (text: string) => boolean;
}

const WildcardTextInput = ({
  text,
  type,
  showPreview,
  textAreaRef,
  inputRef,
  allowFillInTheBlanks,
  previewMode,
  placeholder = '',
  errorMessage,
  handleChange,
  setShowPreview,
  validate,
}: IWildcardTextInputProps): JSX.Element => {
  const handleBlur = (): void => {
    if (!setShowPreview) return;
    // needs to wait to see if user is clicking on wildcard button
    // if so, showPreview gets set to undefined and we don't want to reset it to true
    setTimeout(() => {
      setShowPreview((current) => (current !== undefined ? true : undefined));
    }, 250);
  };

  // split on {{wildcard}}, new line, or new word
  const segments = useMemo(() => text?.split(/({{.+?}})|(\n{1,})|(.+? )/g).filter(Boolean) || [], [text]);

  const handlePreviewClick = (): void => {
    if (!setShowPreview) return;
    setShowPreview(false);
  };

  const handleTextChange = useCallback(
    (newValue: string): void => {
      if (handleChange) {
        handleChange(newValue);
      }
    },
    [handleChange],
  );

  const removeWildcard = useCallback(
    (value: WildcardValues, startIndex: number): void => {
      const start = text.slice(0, startIndex);
      const end = text.slice(startIndex).replace(value, '');
      handleTextChange(start + end);
    },
    [handleTextChange, text],
  );

  const getWildcardTag = useCallback(
    (wildcard: string, startIndex: number): JSX.Element | string => {
      const wildcardValue = Object.values(WildcardValues).find((value) => value === wildcard);

      if (wildcardValue === WildcardValues.FillInTheBlank && !allowFillInTheBlanks) {
        return <Text>{wildcard}</Text>;
      }
      return wildcardValue ? (
        <WildcardTag
          key={cuid()}
          value={wildcardValue}
          startIndex={startIndex}
          previewMode={!!previewMode}
          removeWildcard={removeWildcard}
        />
      ) : linkify.find(wildcard).length && previewMode ? (
        <Text key={cuid()} as="span" variant="interactive">
          <Linkify tagName="span" options={{ target: '_blank' }}>
            {wildcard}
          </Linkify>
        </Text>
      ) : (
        <React.Fragment key={cuid()}>{wildcard}</React.Fragment>
      );
    },
    [previewMode, removeWildcard, allowFillInTheBlanks],
  );

  const formattedContent = useMemo((): JSX.Element => {
    return segments.length ? (
      <Box as="span">
        {segments.map((string, index) => {
          const startIndex = segments.slice(0, index).reduce((prev, next) => (prev += next.length), 0);

          // insert zero-height element for single new line, or (n-1)*lineHeight element for (n) new lines
          // don't do in previewMode since forms remove new lines
          return string === '\n' && !previewMode ? (
            <Box key={`segment_${index}`} width="100%" />
          ) : string.includes('\n') && !previewMode ? (
            <Box key={`segment_${index}`} width="100%">
              {string.replace('\n', '')}
            </Box>
          ) : (
            getWildcardTag(string, startIndex)
          );
        })}
      </Box>
    ) : (
      <Box as="span" opacity={0.3}>
        {placeholder}
      </Box>
    );
  }, [previewMode, getWildcardTag, placeholder, segments]);

  const previewHeight = useMemo((): string => {
    if (previewMode) return 'auto';

    switch (type) {
      case WildcardInputQuestionTypes.Intro:
        return '143px';
      case WildcardInputQuestionTypes.Message:
        return '84px';
      default:
        return '40px';
    }
  }, [previewMode, type]);

  const previewPadding = useMemo(() => {
    if (previewMode) return '0px';

    return '8px 16px';
  }, [previewMode]);

  const isValid = useMemo(() => {
    if (!validate) return true;

    return validate(text);
  }, [text, validate]);

  const showFillInTheBlankError = useMemo(
    () => (!allowFillInTheBlanks && text.includes(WildcardValues.FillInTheBlank)) || false,
    [allowFillInTheBlanks, text],
  );

  return (
    <VStack mb={previewMode ? '0px' : '16px'}>
      {showPreview || previewMode ? (
        <Box
          onClick={handlePreviewClick}
          border="1px"
          borderColor={!isValid && showPreview ? 'border.danger' : previewMode ? 'transparent' : 'border.default'}
          borderRadius="7px"
          bg="background.default"
          color={previewMode === WildcardInputPreviewModes.Form ? 'text.subtle' : 'text.default'}
          fontSize="md"
          p={previewPadding}
          resize="none"
          width="100%"
          height={previewHeight}
          lineHeight="inherit"
          minHeight="20px"
          whiteSpace="pre-wrap"
          overflow="auto"
        >
          {formattedContent}
        </Box>
      ) : type === WildcardInputQuestionTypes.Question ? (
        <TextInput
          ref={inputRef}
          value={text}
          placeholder={placeholder}
          onChange={(e): void => handleTextChange(e.target.value)}
          onBlur={handleBlur}
        />
      ) : (
        <Textarea
          ref={textAreaRef}
          rows={type === WildcardInputQuestionTypes.Intro ? 5 : 3}
          value={text}
          placeholder={placeholder}
          onChange={(e): void => handleTextChange(e.target.value)}
          onBlur={handleBlur}
          resize={type === WildcardInputQuestionTypes.Intro ? 'none' : 'vertical'}
        />
      )}
      {showFillInTheBlankError && (
        <Box fontSize="sm" color="text.danger" alignSelf="flex-start">
          {`This field does not support "Fill In the Blank" wildcards. Please remove these before saving your form.`}
        </Box>
      )}
      {!isValid && showPreview && (
        <Box fontSize="sm" color="text.danger" alignSelf="flex-start">
          {errorMessage}
        </Box>
      )}
    </VStack>
  );
};

export default WildcardTextInput;
